import { useCallback, useEffect, useState } from "preact/hooks";

import type { UsageReport, UsageReportClient } from "../analytics/usage-client";
import { Chip } from "./Chip";

export function UsageAnalyticsView({ client }: { client: UsageReportClient | null }) {
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(false);
    try {
      setReport(await client.load());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (!client) return;
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [client, refresh]);

  const exportCsv = useCallback(async () => {
    if (!client) return;
    setExportStatus("Preparing CSV…");
    try {
      await client.downloadCsv();
      setExportStatus("CSV download started.");
    } catch {
      setExportStatus("Couldn’t export CSV. Try again when you have signal.");
    }
  }, [client]);

  return (
    <section data-testid="usage-analytics" style="display:flex;flex-direction:column;gap:12px">
      <div style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">Pilot usage</div>
      {!client && <p>Usage reporting is unavailable in this build.</p>}
      {client && !report && !error && <p>Loading usage…</p>}
      {error && <p role="alert">Couldn’t load usage. Try again when you have signal.</p>}
      {report && (
        <>
          <p data-testid="usage-timezone" style="margin:0;color:var(--mark-text-muted)">
            Through {report.as_of} · {report.timezone}
          </p>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">
            <strong data-testid="usage-dau">DAU {report.dau}</strong>
            <strong>WAU {report.wau}</strong>
            <strong>MAU {report.mau}</strong>
          </div>
          <p style="margin:0">Active minutes (30 days): {report.active_minutes}</p>
          <small>DAU: {report.windows.dau}</small>
          <small data-testid="usage-window-wau">WAU: {report.windows.wau}</small>
          <small>MAU: {report.windows.mau}</small>
          <div style="display:flex;flex-direction:column;gap:8px" data-testid="usage-actors">
            <strong>30-day activity by user</strong>
            {report.actors.map((actor) => (
              <article
                key={actor.actor_id}
                data-testid={`usage-actor-${actor.actor_id}`}
                style="border:1px solid var(--mark-border-default);border-radius:12px;padding:12px;display:flex;flex-direction:column;gap:8px"
              >
                <strong>{actor.actor_id}</strong>
                <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;font-size:14px">
                  <span>Captures saved: {actor.captures_saved}</span>
                  <span>Reports filed: {actor.reports_filed}</span>
                  <span>Actions changed: {actor.actions_changed}</span>
                  <span>Decisions changed: {actor.decisions_changed}</span>
                </div>
                <small style="color:var(--mark-text-muted)">
                  Active {actor.active_minutes} min · Last active {actor.last_active}
                </small>
              </article>
            ))}
          </div>
          <Chip
            data-testid="usage-refresh"
            disabled={loading}
            onClick={() => void refresh()}
          >
            {loading ? "Refreshing…" : "Refresh usage"}
          </Chip>
          <Chip
            data-testid="usage-download-csv"
            onClick={() => void exportCsv()}
          >
            Download CSV
          </Chip>
          {exportStatus && <p role="status" data-testid="usage-export-status">{exportStatus}</p>}
        </>
      )}
    </section>
  );
}
